import { analyzeConditions, parseActionText } from './actionAnalyzer.js';

const EXECUTABLE_ACTION_TYPES = new Set([
  'call',
  'command',
  'compute',
  'default',
  'exit',
  'stop',
  'return',
  'goto',
  'if',
  'read',
  'remark',
  'require',
  'showMenu',
  'showWindow',
  'ssr',
  'window',
  'write',
]);
const SSR_TEMPLATE_PATTERN = /^(?:\{[^}]+\})?(?:3SSR\b|SSR\b|3[A-Z0-9]{2,}|[A-Z0-9]{4}\/)/i;

function compactObject(value) {
  return Object.fromEntries(
    Object.entries(value).filter(([, entryValue]) => {
      if (entryValue === undefined || entryValue === null) {
        return false;
      }

      if (Array.isArray(entryValue) && entryValue.length === 0) {
        return false;
      }

      return true;
    })
  );
}

function createDuplicateReport() {
  return {
    formsSkipped: 0,
    fieldsSkipped: 0,
    optionsSkipped: 0,
    actionsSkipped: 0,
    commandsSkipped: 0,
  };
}

function normalizeBlockName(name = '') {
  return String(name || '')
    .replace(/:$/, '')
    .trim()
    .replace(/^(?:SUBROUTINE|SUB)\s+/i, '')
    .trim()
    .toUpperCase();
}

function cleanTarget(value = '') {
  const cleaned = normalizeBlockName(String(value || ''));
  const match = cleaned.match(/^([A-Z0-9_.-]+)/i);

  return match ? normalizeBlockName(match[1]) : '';
}

function safeMenuValue(text = '') {
  return String(text || '')
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function getMenuFormId(menu = {}) {
  return safeMenuValue(menu.id || menu.key || menu.title || 'MENU') || 'MENU';
}

function getMenuRoots(ir = {}) {
  const menus = ir.menus || [];

  if ((ir.menuTree || []).length > 0) {
    return ir.menuTree;
  }

  const roots = menus.filter((menu) => !menu.parentKey && !menu.parent && !menu.parentId);

  return roots.length > 0 ? roots : menus;
}

function getSourceLine(action = {}) {
  return action.sourceLine ?? action.line;
}

function getRaw(action = {}) {
  return action.raw || action.normalizedRaw || action.expression || '';
}

function cleanActionIdPart(value = '') {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9@#_.-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);
}

function getActionStableTarget(action = {}, normalized = {}) {
  return (
    normalized.target ||
    normalized.template ||
    normalized.expression ||
    normalized.value ||
    action.target ||
    action.expression ||
    action.template ||
    action.value ||
    ''
  );
}

function buildActionId(action = {}, normalized = {}, scopeId = '') {
  const line = getSourceLine(action) ?? 'unknown';
  const type = normalized.type || action.type || 'action';
  const target = cleanActionIdPart(getActionStableTarget(action, normalized) || getRaw(action));
  const scope = cleanActionIdPart(scopeId || 'global');

  return [scope, `line_${line}`, type, target].filter(Boolean).join('_');
}

function buildActionIdentity(action = {}, normalized = {}, scopeId = '') {
  return [
    scopeId,
    getSourceLine(action) ?? '',
    normalized.type || action.type || '',
    getActionStableTarget(action, normalized),
    getRaw(action),
  ].join('|');
}

function addActionMetadata(normalized, source, scopeId) {
  if (!normalized) {
    return null;
  }

  return compactObject({
    ...normalized,
    sourceLine: getSourceLine(source),
    raw: getRaw(source),
    actionId: buildActionId(source, normalized, scopeId),
  });
}

function isExecutionBlock(block) {
  return block && block.blockType !== 'metadata';
}

function hasExecutableActions(block) {
  return (block?.actions || []).some((action) => action && EXECUTABLE_ACTION_TYPES.has(action.type));
}

function resolveEntryBlock(blocks = {}) {
  if (isExecutionBlock(blocks.START) && hasExecutableActions(blocks.START)) {
    return 'START';
  }

  if (isExecutionBlock(blocks.INICIO) && hasExecutableActions(blocks.INICIO)) {
    return 'INICIO';
  }

  if (isExecutionBlock(blocks.INICIAR) && hasExecutableActions(blocks.INICIAR)) {
    return 'INICIAR';
  }

  const firstTopLevelBlock = Object.values(blocks).find(
    (block) => isExecutionBlock(block) && !isSubroutineBlock(block) && hasExecutableActions(block)
  );

  if (firstTopLevelBlock) {
    return normalizeBlockName(firstTopLevelBlock.name);
  }

  const firstExecutableBlock = Object.values(blocks).find((block) => isExecutionBlock(block) && hasExecutableActions(block));

  return firstExecutableBlock ? firstExecutableBlock.name : null;
}

function actionContainsType(action, types) {
  if (!action) {
    return false;
  }

  if (types.has(action.type)) {
    return true;
  }

  return ['then', 'else'].some((key) => {
    return Array.isArray(action[key]) && action[key].some((childAction) => actionContainsType(childAction, types));
  });
}

function resolveEntryBlockFromExecutableBlocks(blocks = {}) {
  if (Array.isArray(blocks.START) && blocks.START.length > 0) {
    return 'START';
  }

  if (Array.isArray(blocks.INICIO) && blocks.INICIO.length > 0) {
    return 'INICIO';
  }

  if (Array.isArray(blocks.INICIAR) && blocks.INICIAR.length > 0) {
    return 'INICIAR';
  }

  const windowTypes = new Set(['showWindow', 'window']);
  const blockWithWindow = Object.entries(blocks).find(([, actions]) => {
    return Array.isArray(actions) && actions.some((action) => actionContainsType(action, windowTypes));
  });

  if (blockWithWindow) {
    return blockWithWindow[0];
  }

  const firstExecutableBlock = Object.entries(blocks).find(([, actions]) => Array.isArray(actions) && actions.length > 0);

  return firstExecutableBlock ? firstExecutableBlock[0] : null;
}

function findWindow(forms = [], action = {}) {
  const target = action.target || action.formRef || action.id;

  return (
    forms.find((form) => form.id === target) ||
    forms.find((form) => form.title && action.title && form.title === action.title) ||
    null
  );
}

function normalizeConditions(conditions = []) {
  return conditions.map((condition) => compactObject({ ...condition }));
}

function normalizeCommand(action) {
  const template = action.template || action.expression;

  if (isSsrTemplate(template)) {
    return normalizeSsr({ ...action, template });
  }

  return compactObject({
    type: 'command',
    template,
    submit: action.submit,
  });
}

function isSsrTemplate(template = '') {
  return SSR_TEMPLATE_PATTERN.test(String(template || '').trim());
}

function extractSsrCode(template = '') {
  const withoutDynamicPrefix = String(template || '').trim().replace(/^\{[^}]+\}/, '');
  const match = withoutDynamicPrefix.match(/^(?:3SSR\s+|SSR\s+|3)?([A-Z0-9]{4})\b/i);

  return match ? match[1].toUpperCase() : undefined;
}

function normalizeVariableLookupName(name = '') {
  return String(name || '').replace(/#[A-Z]$/i, '').replace(/^@/, '').toUpperCase();
}

function findReadTargetVariable(ir = {}, target = '') {
  const targetName = normalizeVariableLookupName(target);

  if (!targetName) {
    return null;
  }

  const directVariable = ir.variables?.[target] || ir.variables?.[targetName];

  if (directVariable) {
    return directVariable;
  }

  return Object.values(ir.variables || {}).find((variable) => {
    return normalizeVariableLookupName(variable.name || variable.target || variable.sourceVariable) === targetName;
  }) || null;
}

function getReadTargetMeta(action = {}, ir = {}) {
  const targets = action.targets && action.targets.length > 0
    ? action.targets
    : action.target
      ? [action.target]
      : [];
  const targetLengths = {};
  const targetMeta = {};

  targets.forEach((target) => {
    const variable = findReadTargetVariable(ir, target);
    const length = Number(variable?.length ?? variable?.maxlength ?? 0);

    if (Number.isFinite(length) && length > 0) {
      targetLengths[target] = length;
      targetMeta[target] = {
        length,
        datatype: variable?.datatype,
      };
    }
  });

  return {
    targetLengths: Object.keys(targetLengths).length > 0 ? targetLengths : undefined,
    targetMeta: Object.keys(targetMeta).length > 0 ? targetMeta : undefined,
  };
}

function normalizeRead(action, ir = {}) {
  const targetMetadata = getReadTargetMeta(action, ir);

  return compactObject({
    type: 'read',
    source: action.source,
    row: action.row,
    rowRef: action.rowRef,
    column: action.column,
    columnRef: action.columnRef,
    search: action.search,
    target: action.target || action.targets?.[0],
    targets: action.targets && action.targets.length > 0 ? action.targets : undefined,
    parseParts: action.parseParts,
    until: action.until,
    targetLengths: targetMetadata.targetLengths,
    targetMeta: targetMetadata.targetMeta,
  });
}

function normalizeDefault(action) {
  return compactObject({
    type: 'default',
    defaultType: action.defaultType,
    target: action.target,
    suffix: action.suffix,
    targetSuffix: action.targetSuffix,
    value: action.value,
    valueSuffix: action.valueSuffix,
    valueType: action.valueType,
    isSelfAssignment: action.isSelfAssignment,
    catalogId: action.catalogId,
    options: action.options,
    source: action.source,
    path: action.path,
  });
}

function normalizeRemark(action) {
  const template = String(action.template || '').replace(/\s+\b(?:ENDIF|ELSE|THEN)\b.*$/i, '').trim();

  return compactObject({
    type: 'remark',
    template,
    submit: action.submit,
    remarkType: action.remarkType || 'Historical',
    skip: action.skip !== false,
  });
}

function normalizeSsr(action) {
  const template = String(action.template || action.expression || '')
    .replace(/\s+\b(?:ENDIF|ELSE|THEN)\b.*$/i, '')
    .trim();

  return compactObject({
    type: 'ssr',
    code: extractSsrCode(template),
    template,
    submit: action.submit,
    block: 3,
  });
}

function normalizeGoto(action) {
  return compactObject({
    type: 'goto',
    target: cleanTarget(action.target || action.expression),
  });
}

function normalizeCall(action) {
  return compactObject({
    type: 'call',
    target: cleanTarget(action.target || action.expression),
  });
}

function normalizeShowWindow(action, ir) {
  const window = findWindow(ir.forms || [], action);
  const target = action.target || action.formRef || action.id || window?.id;
  const legacyTarget = action.legacyTarget || action.legacyId || window?.legacyId;

  return compactObject({
    type: 'showWindow',
    target,
    legacyTarget,
    title: action.title || window?.title,
    windowType: window?.windowType,
  });
}

function normalizeShowMenu(action) {
  return compactObject({
    type: 'showMenu',
    target: action.target || action.id || action.menuId,
    title: action.title,
  });
}

function parseInlineIfBranches(expression = '') {
  const source = String(expression || '');
  const thenMatch = source.match(/\bTHEN\b/i);
  if (!thenMatch) return { thenAction: null, elseAction: null };

  const afterThen = source.slice(thenMatch.index + thenMatch[0].length).trim();
  const elseMatch = afterThen.match(/\bELSE\b/i);
  const thenText = (elseMatch ? afterThen.slice(0, elseMatch.index) : afterThen)
    .replace(/\bENDIF\b.*$/i, '')
    .trim();
  const elseText = (elseMatch ? afterThen.slice(elseMatch.index + elseMatch[0].length) : '')
    .replace(/\bENDIF\b.*$/i, '')
    .trim();

  return {
    thenAction: thenText ? parseActionText(thenText) : null,
    elseAction: elseText ? parseActionText(elseText) : null,
  };
}

function normalizeIf(action, ir, scopeId = '', duplicates = null) {
  const ifScope = `${scopeId}:if:${getSourceLine(action) ?? 'unknown'}`;
  const inline = parseInlineIfBranches(action.expression || getRaw(action));
  const thenSource = Array.isArray(action.then) && action.then.length > 0
    ? action.then
    : action.action
      ? [action.action]
      : inline.thenAction
        ? [inline.thenAction]
        : [];
  const elseSource = Array.isArray(action.else) && action.else.length > 0
    ? action.else
    : action.falseAction
      ? [action.falseAction]
      : inline.elseAction
        ? [inline.elseAction]
        : [];
  const thenActions = normalizeActions(thenSource, ir, `${ifScope}:then`, duplicates);
  const elseActions = normalizeActions(elseSource, ir, `${ifScope}:else`, duplicates);
  const conditionSource = action.expression || getRaw(action).replace(/^\s*IF\b/i, '').trim();
  const conditionAnalysis = analyzeConditions(conditionSource);
  const conditions = Array.isArray(action.conditions) && action.conditions.length > 0
    ? action.conditions
    : conditionAnalysis.conditions;

  return compactObject({
    type: 'if',
    conditions: normalizeConditions(conditions),
    conditionParseErrors: conditionAnalysis.invalidSegments,
    then: thenActions,
    else: elseActions,
  });
}

function normalizeMenuSelectionAssignment(action, scopeId = '') {
  if (!String(scopeId).startsWith('MENU_')) {
    return null;
  }

  const source = String(action.raw || action.expression || '').trim();
  const match = source.match(
    /^IF\s+\[([^\]]+)\]\s*(?:<>|!=)\s*(?:"\s*"|'\s*')\s+THEN\s+\[([^\]]+)\]\s*=\s*(.*?)\s+ENDIF\s*$/i
  );

  if (!match || normalizeVariableLookupName(match[1]) !== normalizeVariableLookupName(match[2])) {
    return null;
  }

  const rawValue = String(match[3] || '').trim();
  const quotedValue = rawValue.match(/^(?:"([\s\S]*)"|'([\s\S]*)')$/);

  return {
    type: 'default',
    defaultType: 'menu_selection',
    target: match[2],
    value: quotedValue ? quotedValue[1] ?? quotedValue[2] ?? '' : rawValue,
    valueType: 'literal',
  };
}

function normalizeAction(action, ir, scopeId = '', duplicates = null) {
  if (!action || !EXECUTABLE_ACTION_TYPES.has(action.type)) {
    return null;
  }

  if (action.type === 'command') {
    return normalizeCommand(action);
  }

  if (action.type === 'read') {
    return normalizeRead(action, ir);
  }

  if (action.type === 'default') {
    return normalizeDefault(action);
  }

  if (action.type === 'remark') {
    return normalizeRemark(action);
  }

  if (action.type === 'goto') {
    return normalizeGoto(action);
  }

  if (action.type === 'call') {
    return normalizeCall(action);
  }

  if (action.type === 'exit' || action.type === 'stop') {
    return { type: action.type };
  }

  if (action.type === 'return') {
    return { type: 'return' };
  }

  if (action.type === 'if') {
    return normalizeMenuSelectionAssignment(action, scopeId) || normalizeIf(action, ir, scopeId, duplicates);
  }

  if (action.type === 'window' || action.type === 'showWindow') {
    return normalizeShowWindow(action, ir);
  }

  if (action.type === 'showMenu') {
    return normalizeShowMenu(action);
  }

  if (action.type === 'ssr') {
    return normalizeSsr(action);
  }

  if (['compute', 'require', 'write'].includes(action.type)) {
    return compactObject({
      type: action.type,
      expression: action.expression,
      target: action.target,
    });
  }

  return null;
}

function getMenuItems(menu = {}) {
  return menu.items || menu.options || [];
}

function getMenuOptionActionType(item = {}) {
  if (['submenu', 'actions', 'call', 'goto', 'exit', 'stop', 'showWindow'].includes(item.actionType)) {
    return item.actionType;
  }

  const actions = item.actions || [];
  const submenuAction = actions.find((action) => action.type === 'submenu') || (item.action?.type === 'submenu' ? item.action : null);
  const executableActions = actions.filter((action) => action.type !== 'submenu');

  if (submenuAction) {
    return 'submenu';
  }

  if (executableActions.length === 0) {
    return 'unknown';
  }

  if (executableActions.length === 1 && ['call', 'goto', 'exit', 'stop', 'window', 'showWindow'].includes(executableActions[0].type)) {
    return executableActions[0].type === 'window' ? 'showWindow' : executableActions[0].type;
  }

  return 'actions';
}

function getMenuActionRef(menu, item) {
  return item.actionRef || `${getMenuFormId(menu)}_OPTION_${safeMenuValue(item.text || item.value)}`;
}

function collectMenus(menus = []) {
  const collected = [];
  const seenMenuIds = new Set();

  function visit(menu) {
    if (!menu) {
      return;
    }

    const menuId = getMenuFormId(menu);

    if (seenMenuIds.has(menuId)) {
      return;
    }

    seenMenuIds.add(menuId);
    collected.push(menu);

    getMenuItems(menu).forEach((item) => {
      if (item.submenu) {
        visit(item.submenu);
        return;
      }

      (item.actions || []).forEach((action) => {
        if (action.type === 'submenu' && action.menu) {
          visit(action.menu);
        }
      });
    });
  }

  menus.forEach(visit);
  return collected;
}

function buildMenuActions(ir, duplicates = null) {
  const roots = getMenuRoots(ir);
  const menuActions = {};

  collectMenus(roots).forEach((menu) => {
    getMenuItems(menu).forEach((item) => {
      const actionRef = getMenuActionRef(menu, item);
      let actions = (item.actions || []).filter((action) => action.type !== 'submenu');
      if (actions.length === 0 && getMenuOptionActionType(item) === 'unknown') {
        const followingBlock = Object.entries(ir.blocks || {}).find(([, block]) => (
          isExecutionBlock(block) && Number(block.line) === Number(item.line) + 1
        ));
        if (followingBlock) {
          actions = [{
            type: 'goto',
            target: followingBlock[0],
            line: followingBlock[1].line,
            raw: `GOTO ${followingBlock[0]} (inferido por opcion seguida de etiqueta)`,
          }];
        }
      }
      if (actions.length === 0) return;

      const normalizedActions = normalizeActions(actions, ir, actionRef, duplicates);

      if (normalizedActions.length === 0) {
        return;
      }

      if (!menuActions[actionRef]) {
        menuActions[actionRef] = normalizedActions;
      }
    });
  });

  return menuActions;
}

function buildRuntimeMenus(ir = {}, menuActions = {}) {
  const menus = {};
  collectMenus(getMenuRoots(ir)).forEach((menu) => {
    const id = getMenuFormId(menu);
    menus[id] = compactObject({
      id,
      title: menu.title,
      sourceLine: menu.line ?? menu.sourceLine,
      options: getMenuItems(menu)
        .filter((item) => item?.visible !== false)
        .map((item) => {
          const submenuAction = (item.actions || []).find((action) => action.type === 'submenu')
            || (item.action?.type === 'submenu' ? item.action : null);
          const submenu = item.submenu || submenuAction?.menu;
          const actionRef = getMenuActionRef(menu, item);
          return compactObject({
            text: item.text || item.label || item.value,
            value: item.value || item.text || item.label,
            actionRef: menuActions[actionRef] ? actionRef : undefined,
            targetMenu: submenu?.id || submenu?.key || submenuAction?.targetId || submenuAction?.target,
            terminal: ['exit', 'stop', 'return'].includes(item.actionType)
              || (item.actions || []).some((action) => ['exit', 'stop', 'return'].includes(action.type)),
          });
        }),
    });
  });
  return menus;
}

function buildRuntimeForms(ir = {}) {
  const normalizeRuntimeField = (field = {}) => compactObject({
    type: field.type,
    control: field.control,
    controlType: field.controlType,
    label: field.label || field.text,
    text: field.text,
    prompt: field.prompt,
    helperText: field.helperText,
    name: field.name,
    variable: field.variable,
    binding: field.binding || field.variable,
    variableBase: field.variableBase,
    editable: field.editable,
    capturable: field.capturable,
    readonly: field.readonly,
    disabled: field.disabled,
    required: field.required,
    mandatory: field.mandatory,
    visualRequired: field.visualRequired,
    requiredOne: field.requiredOne,
    datatype: field.datatype,
    typedata: field.typedata,
    maxlength: field.maxlength,
    length: field.length,
    value: field.value,
    placeholder: field.placeholder,
    templateValue: field.templateValue,
    textTemplate: field.textTemplate,
    displayVariable: field.displayVariable,
    informational: field.informational,
    action: field.action || field.button,
    button: field.button,
    buttonDefinition: field.buttonDefinition,
    catalog: field.catalog,
    catalogMissing: field.catalogMissing,
    catalogSource: field.catalogSource,
    options: field.options,
    parts: Array.isArray(field.parts) ? field.parts.map(normalizeRuntimeField) : undefined,
    visible: field.visible,
    line: field.line ?? field.sourceLine,
    raw: field.raw,
    sourceLine: field.sourceLine ?? field.line,
  });

  return (ir.forms || []).reduce((forms, form) => {
    if (!form?.id || form.visible === false) return forms;
    forms[form.id] = compactObject({
      id: form.id,
      legacyId: form.legacyId,
      title: form.title,
      sourceLine: form.sourceLine ?? form.line,
      windowType: form.windowType,
      fields: (form.fields || []).map(normalizeRuntimeField),
    });
    return forms;
  }, {});
}

function normalizeActions(actions = [], ir, scopeId = 'global', duplicates = null) {
  const seenActionKeys = new Set();
  const normalizedActions = [];

  actions.forEach((sourceAction) => {
    const normalizedAction = normalizeAction(sourceAction, ir, scopeId, duplicates);

    if (!normalizedAction) {
      return;
    }

    const identity = buildActionIdentity(sourceAction, normalizedAction, scopeId);

    if (seenActionKeys.has(identity)) {
      if (duplicates) {
        duplicates.actionsSkipped += 1;

        if (normalizedAction.type === 'command') {
          duplicates.commandsSkipped += 1;
        }
      }

      return;
    }

    seenActionKeys.add(identity);
    normalizedActions.push(addActionMetadata(normalizedAction, sourceAction, scopeId));
  });

  return normalizedActions;
}

function buildExecutableBlocks(ir, duplicates = null) {
  const sourceBlocks = ir.blocks || {};

  if (Object.keys(sourceBlocks).length === 0) {
    return {};
  }

  return Object.entries(sourceBlocks)
    .filter(([, block]) => isExecutionBlock(block))
    .reduce((blocks, [name, block]) => {
      const normalizedName = normalizeBlockName(name);
      const normalizedActions = normalizeActions(block.actions || [], ir, normalizedName, duplicates);

      blocks[normalizedName] = blocks[normalizedName] || [];
      blocks[normalizedName].push(...normalizedActions);

      return blocks;
    }, {});
}

function isSubroutineBlock(block = {}) {
  return /^(?:SUB|SUBROUTINE)\b/i.test(String(block.raw || '').trim());
}

function isExitLikeBlockName(name = '') {
  return /^(?:SALIR|EXIT|FIN|END|RETURN|STOP)$/i.test(String(name || '').trim());
}

function isTerminalAction(action = {}) {
  if (!action || !action.type) {
    return false;
  }

  if (['goto', 'exit', 'return', 'stop', 'showMenu'].includes(action.type)) {
    return true;
  }

  if (action.type === 'if') {
    const thenActions = Array.isArray(action.then) ? action.then : [];
    const elseActions = Array.isArray(action.else) ? action.else : [];
    const thenTerminal = thenActions.length > 0 && isTerminalAction(thenActions[thenActions.length - 1]);
    const elseTerminal = elseActions.length > 0 && isTerminalAction(elseActions[elseActions.length - 1]);

    return thenTerminal && elseTerminal;
  }

  return false;
}

function collectDirectBlockReferencesFromAction(action = {}, references = []) {
  if (!action || !action.type) {
    return references;
  }

  if (['goto', 'call'].includes(action.type) && action.target) {
    references.push(normalizeBlockName(action.target));
  }

  ['then', 'else'].forEach((key) => {
    (action[key] || []).forEach((childAction) => collectDirectBlockReferencesFromAction(childAction, references));
  });

  return references;
}

function getBlockReferenceSources(blocks = {}) {
  const referenceSources = {};

  Object.entries(blocks).forEach(([blockName, actions]) => {
    (actions || [])
      .flatMap((action) => collectDirectBlockReferencesFromAction(action))
      .forEach((target) => {
        referenceSources[target] = referenceSources[target] || new Set();
        referenceSources[target].add(blockName);
      });
  });

  return referenceSources;
}

function hasUnsafeInboundReferences(referenceSources = {}, targetName = '', currentName = '') {
  const sources = referenceSources[targetName] || new Set();

  return [...sources].some((sourceName) => sourceName !== currentName && sourceName !== targetName);
}

function canGenerateLinearFlowTransition(currentBlock = {}, currentActions = [], nextBlock = {}, context = {}) {
  const currentName = normalizeBlockName(currentBlock.name);
  const nextName = normalizeBlockName(nextBlock.name);
  const currentSubroutine = normalizeBlockName(currentBlock.parentSubroutine);
  const nextSubroutine = normalizeBlockName(nextBlock.parentSubroutine);
  const sameSubroutine = Boolean(currentSubroutine && currentSubroutine === nextSubroutine);

  if (!isExecutionBlock(currentBlock) || !isExecutionBlock(nextBlock)) {
    return false;
  }

  if (!currentName || !nextName || currentName === nextName) {
    return false;
  }

  if ((currentActions.length === 0 && !sameSubroutine) || !context.blocks?.[nextName]) {
    return false;
  }

  if ((isSubroutineBlock(currentBlock) || isSubroutineBlock(nextBlock)) && !sameSubroutine) {
    return false;
  }

  if (isExitLikeBlockName(nextName)) {
    return false;
  }

  if (currentActions.some((action) => actionContainsType(action, new Set(['showMenu'])))) {
    return false;
  }

  if (isTerminalAction(currentActions[currentActions.length - 1])) {
    return false;
  }

  if (!sameSubroutine && hasUnsafeInboundReferences(context.referenceSources, nextName, currentName)) {
    return false;
  }

  return true;
}

function appendLinearFlowTransitions(blocks = {}, sourceBlocks = {}, duplicates = null) {
  const orderedExecutionBlocks = Object.entries(sourceBlocks).filter(([, block]) => isExecutionBlock(block));
  const referenceSources = getBlockReferenceSources(blocks);

  orderedExecutionBlocks.forEach(([name, block], index) => {
    const nextEntry = orderedExecutionBlocks[index + 1];

    if (!nextEntry) {
      return;
    }

    const normalizedName = normalizeBlockName(name);
    const actions = blocks[normalizedName] || [];
    const [nextName, nextBlock] = nextEntry;
    const normalizedNextName = normalizeBlockName(nextName);

    if (!canGenerateLinearFlowTransition(block, actions, nextBlock, { blocks, referenceSources })) {
      return;
    }

    const source = {
      type: 'goto',
      target: normalizedNextName,
      line: block.endLine || block.line,
      sourceLine: block.endLine || block.line,
      raw: `LINEAR FLOW ${normalizedName} -> ${normalizedNextName}`,
    };
    const normalized = {
      type: 'goto',
      target: normalizedNextName,
      generated: true,
      reason: 'linear_flow_from_sst',
    };

    actions.push(addActionMetadata(normalized, source, normalizedName));

    if (duplicates) {
      duplicates.linearFlowTransitions = (duplicates.linearFlowTransitions || 0) + 1;
    }
  });

  return blocks;
}

function buildMetadata(ir, duplicates = null) {
  const metadataBlocks = Object.fromEntries(
    Object.entries(ir.blocks || {})
      .filter(([, block]) => !isExecutionBlock(block))
      .map(([name, block]) => [
        normalizeBlockName(name),
        compactObject({
          name: block.name,
          blockType: block.blockType,
          line: block.line,
          actions: normalizeActions(block.actions || [], ir, `metadata:${normalizeBlockName(name)}`, duplicates),
        }),
      ])
  );

  return compactObject({
    blocks: Object.keys(metadataBlocks).length > 0 ? metadataBlocks : undefined,
    buttonDefinitions: Object.keys(ir.buttonDefinitions || {}).length > 0 ? ir.buttonDefinitions : undefined,
  });
}

function collectGotoTargetsFromAction(action, targets = []) {
  if (!action) {
    return targets;
  }

  if (action.type === 'goto' && action.target) {
    targets.push(action.target);
  }

  ['then', 'else'].forEach((key) => {
    (action[key] || []).forEach((childAction) => collectGotoTargetsFromAction(childAction, targets));
  });

  return targets;
}

function collectGotoTargets(blocks) {
  return Object.values(blocks).flatMap((actions) => {
    return actions.flatMap((action) => collectGotoTargetsFromAction(action));
  });
}

function validateGotoTargets(ir, executableBlocks) {
  const knownBlocks = new Set([...Object.keys(ir.blocks || {}).map(normalizeBlockName), ...Object.keys(executableBlocks || {})]);

  return collectGotoTargets(executableBlocks)
    .filter((target) => !knownBlocks.has(normalizeBlockName(target)))
    .map((target) => ({
      level: 'warning',
      code: 'GOTO_TARGET_NOT_FOUND',
      target,
    }));
}

function collectCallTargetsFromAction(action, targets = []) {
  if (!action) {
    return targets;
  }

  if (action.type === 'call' && action.target) {
    targets.push(action.target);
  }

  ['then', 'else'].forEach((key) => {
    (action[key] || []).forEach((childAction) => collectCallTargetsFromAction(childAction, targets));
  });

  return targets;
}

function collectCallTargets(blocks) {
  return Object.values(blocks).flatMap((actions) => {
    return actions.flatMap((action) => collectCallTargetsFromAction(action));
  });
}

function validateCallTargets(ir, executableBlocks) {
  const knownBlocks = new Set([...Object.keys(ir.blocks || {}).map(normalizeBlockName), ...Object.keys(executableBlocks || {})]);

  return collectCallTargets(executableBlocks)
    .filter((target) => !knownBlocks.has(normalizeBlockName(target)))
    .map((target) => ({
      level: 'warning',
      code: 'UNRESOLVED_CALL_TARGET',
      target,
    }));
}

function buildFlatFallback(ir, duplicates = null) {
  const actions = normalizeActions(ir.logic || [], ir, 'MAIN', duplicates);

  if (actions.length === 0) {
    return {};
  }

  return {
    MAIN: actions,
  };
}

function buildVariableDefinitions(ir = {}) {
  const definitions = {};
  Object.entries(ir.variables || {}).forEach(([key, variable]) => {
    if (!variable || typeof variable !== 'object') return;
    const name = String(variable.name || key || '').replace(/^@/, '').replace(/#[A-Z]$/i, '').toUpperCase();
    if (!name) return;
    const current = definitions[name] || {};
    definitions[name] = compactObject({
      required: Boolean(current.required || variable.required),
      external: Boolean(current.external || variable.external),
      datatype: current.datatype || variable.datatype,
      length: current.length || variable.length,
      sourceLine: current.sourceLine || variable.line,
      externalLine: current.externalLine || variable.externalLine,
    });
  });
  return definitions;
}

function buildInitializers(blocks = {}) {
  return (blocks.DEFAULTS || []).filter((action) => action?.type === 'default');
}

function recoverDetachedStartupPrelude(blocks = {}, entryBlock = null) {
  if (!entryBlock || blocks.START) return { blocks, entryBlock };

  for (const [blockName, actions] of Object.entries(blocks)) {
    const selfCallIndex = (actions || []).findIndex((action) => (
      action?.type === 'call' && normalizeBlockName(action.target) === normalizeBlockName(blockName)
    ));
    if (selfCallIndex < 0) continue;

    const lastInitializerIndex = actions
      .slice(0, selfCallIndex)
      .reduce((last, action, index) => action?.type === 'default' ? index : last, -1);
    const preludeStart = lastInitializerIndex + 1;
    const prelude = actions.slice(preludeStart, selfCallIndex);
    if (!prelude.some((action) => ['showMenu', 'showWindow'].includes(action?.type))) continue;

    return {
      blocks: {
        ...blocks,
        [blockName]: actions.slice(0, preludeStart),
        START: [...prelude, { type: 'goto', target: entryBlock, synthetic: true }],
      },
      entryBlock: 'START',
    };
  }

  return { blocks, entryBlock };
}

export function generateLogicJson(ir = {}) {
  const sourceBlocks = ir.blocks || {};
  const duplicates = createDuplicateReport();
  const generatedBlocks = Object.keys(sourceBlocks).length > 0
    ? appendLinearFlowTransitions(buildExecutableBlocks(ir, duplicates), sourceBlocks, duplicates)
    : buildFlatFallback(ir, duplicates);
  const sourceEntryBlock = normalizeBlockName(resolveEntryBlock(sourceBlocks));
  const generatedEntryBlock = (sourceEntryBlock && generatedBlocks[sourceEntryBlock] ? sourceEntryBlock : null)
    || resolveEntryBlockFromExecutableBlocks(generatedBlocks)
    || (generatedBlocks.MAIN ? 'MAIN' : null);
  const recoveredProgram = recoverDetachedStartupPrelude(generatedBlocks, generatedEntryBlock);
  const blocks = recoveredProgram.blocks;
  const entryBlock = recoveredProgram.entryBlock;
  const diagnostics = [...validateGotoTargets(ir, blocks), ...validateCallTargets(ir, blocks)];
  const metadata = buildMetadata(ir, duplicates);
  const menuActions = buildMenuActions(ir, duplicates);
  const menus = buildRuntimeMenus(ir, menuActions);
  const forms = buildRuntimeForms(ir);
  const variableDefinitions = buildVariableDefinitions(ir);
  const initializers = buildInitializers(blocks);

  return compactObject({
    entryBlock,
    blocks,
    menuActions: Object.keys(menuActions).length > 0 ? menuActions : undefined,
    menus: Object.keys(menus).length > 0 ? menus : undefined,
    forms: Object.keys(forms).length > 0 ? forms : undefined,
    variableDefinitions: Object.keys(variableDefinitions).length > 0 ? variableDefinitions : undefined,
    initializers: initializers.length > 0 ? initializers : undefined,
    metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
    diagnostics,
    duplicates,
  });
}
